import { resolveConnectNoun } from "@ando/ui";
import { motion } from "framer-motion";
import type * as React from "react";

import type { ConnectionSummary } from "@ando/core";
import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";

import { ConnectedAccountRow } from "@/features/connectors/components";

/**
 * "Account collegati" section — purely presentational. Owns no state and no
 * mutations: every callback is supplied by the route, which is the single
 * source of truth for connection ids, mutation pending flags, and the
 * per-row kebab render-prop factory.
 *
 * Extracted from ``$capability.tsx`` on May 28, 2026 as part of the route
 * slim-down (target ≤ 600 LoC). DOM tree is byte-equivalent to the inline
 * version that lived in the route file.
 */
export interface ConnectedAccountsSectionProps {
  readonly spec: CapabilityWizardSpec<BaseConnection>;
  readonly connections: readonly ConnectionSummary[];
  readonly reducedMotion: boolean;
  readonly disconnectingId: string | null;
  readonly reauthingId: string | null;
  readonly onReauth: (connectionId: string, vendor: string) => void;
  readonly onDisconnect: (connectionId: string, vendor?: string) => void;
  /** Open the management UI (single entry point: ``Gestisci →`` button). */
  readonly onManage: (connection: ConnectionSummary) => void;
  /**
   * Per-row factory for kebab-menu extras — used by DB/ERP routes to inject
   * schema / test / edit / context actions. Returns the
   * ``(close) => ReactNode`` render-prop expected by
   * ``ConnectedAccountRow``.
   */
  readonly renderExtraMenuItems?: (
    connection: ConnectionSummary,
  ) => (close: () => void) => React.ReactNode;
}

export const ConnectedAccountsSection: React.FC<ConnectedAccountsSectionProps> = ({
  spec,
  connections,
  reducedMotion,
  disconnectingId,
  reauthingId,
  onReauth,
  onDisconnect,
  onManage,
  renderExtraMenuItems,
}) => (
  <section className="flex flex-col gap-2">
    <header className="flex items-center gap-2">
      <h2 className="font-semibold text-[10.5px] text-ink-2 uppercase tracking-[0.08em]">
        {resolveConnectNoun(spec).sectionLabel}
      </h2>
      <span className="rounded-full bg-success/15 px-2 py-0.5 font-medium text-[10px] text-success tabular-nums">
        {connections.length}
      </span>
    </header>
    <ul className="flex flex-col gap-1.5">
      {connections.map((c, idx) => (
        <motion.div
          key={c.conn_id}
          initial={reducedMotion ? false : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: reducedMotion ? 0 : 0.16,
            delay: reducedMotion ? 0 : Math.min(idx * 0.03, 0.15),
            ease: "easeOut",
          }}
        >
          <ConnectedAccountRow
            connection={c}
            busy={c.conn_id === disconnectingId || c.conn_id === reauthingId}
            onReauth={() => onReauth(c.conn_id, c.provider ?? c.vendor)}
            onManage={() => onManage(c)}
            onDisconnect={() => onDisconnect(c.conn_id, c.provider ?? c.vendor)}
            extraMenuItems={renderExtraMenuItems?.(c)}
          />
        </motion.div>
      ))}
    </ul>
  </section>
);
ConnectedAccountsSection.displayName = "ConnectedAccountsSection";
